import { Select, useSelectQueryGate } from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { ChevronIcon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Group, Text, useMantineTheme } from '@mantine/core';
import { useState } from 'react';
import { IssueBadge } from '../../components';
import {
  STORYBOOK_BANK_ACCOUNT_SELECT_OPTIONS_QUERY_KEY,
  useBankAccountSelectOptionsQuery,
} from '@vrplatform/data';

type Props = {
  payoutId: string;
  bankAccount: {
    title: string;
    accountId: string;
  } | null;
};

export const STORYBOOK_PAYOUT_BANK_ACCOUNT_SELECT_QUERY_KEY =
  STORYBOOK_BANK_ACCOUNT_SELECT_OPTIONS_QUERY_KEY;

export const PayoutBankAccountSelect = (props: Props) => {
  const { payoutId } = props;
  const gate = useSelectQueryGate({ isActive: false });

  const [newBankAccount, setNewBankAccount] = useState<
    Props['bankAccount'] | undefined
  >(undefined);

  const invalidate = useInvalidateApiQueries(['deposits', 'owner-statements']);

  const { data = [], isLoading: loadingQuery } =
    useBankAccountSelectOptionsQuery({
      skip: !gate.shouldFetchAny,
    });

  const { mutateAsync, isPending: loadingMutation } = useApiMutation(
    'put',
    '/transactions/{id}'
  );

  const updateBankAccount = async (accountId: string | null) =>
    mutateAsync({
      params: {
        path: {
          id: payoutId,
        },
      },
      body: {
        accountId,
      },
    })
      .then((result) => {
        const account = result.data.account;

        if (account) {
          const title = data.find((x) => x.value === account?.id)?.label;

          setNewBankAccount({
            accountId: account.id,
            title: title ?? account.name,
          });
        } else {
          setNewBankAccount(null);
        }
        invalidate();
      })
      .catch((error: unknown) => {
        showApiErrorNotification({
          title: 'Failed to update payout',
          defaultMessage:
            'We failed to update the payout. Please try again later and if the problem persists, contact support.',
          error,
        });
      });

  const bankAccount =
    newBankAccount !== undefined ? newBankAccount : props.bankAccount;

  return (
    <Select
      data={{
        options: data,
        loading: loadingQuery,
      }}
      type="single"
      setValue={(value) => value?.value && updateBankAccount(value.value)}
      value={
        bankAccount?.accountId
          ? { label: bankAccount.title, value: bankAccount.accountId }
          : null
      }
      inputProps={{
        loadingMutation,
        loadingQuery,
      }}
      dropdownProps={{
        withinPortal: true,
        position: 'bottom-start',
        zIndex: 200,
      }}
      onOpenChange={gate.onOpenChange}
    >
      {({ value, type, opened, loadingMutation }) => {
        const { primaryColor, colors, radius } = useMantineTheme();

        if (type !== 'single') return <div />;

        return (
          <Group
            wrap="nowrap"
            gap={8}
            display="inline-flex"
            sx={{
              cursor: 'pointer',
              border: '1px solid',
              borderColor: opened ? colors[primaryColor][6] : 'transparent',
              borderRadius: radius.sm,
              boxShadow: opened
                ? `0px 0px 0px 2px ${colors[primaryColor][4]}40`
                : undefined,
              color: colors[primaryColor][7],
              // paddingBlock: `2px ${spacing.sm}`,
            }}
          >
            {!value && <IssueBadge status="error" />}
            <Text lineClamp={1}>{value?.label || 'Select bank account'}</Text>
            {loadingMutation ? (
              <LoadingIndicator size={'0.75rem'} />
            ) : (
              <ChevronIcon color={colors[primaryColor][7]} size={16} />
            )}
          </Group>
        );
      }}
    </Select>
  );
};
